/* 文章详情页 */
import React from 'react'
import Head from 'next/head'
import {
  IArticle,
  getArticleDetail,
  getRelatedRecommend,
  getRelatedSeries,
  getRecommendSeries,
  IRelatedSeriesItem,
  IRecommendSeriesItem,
  IRelatedRecommendData,
} from './services/index'
import Article from './article'
import { DEFAULT_ARTICLE_DESCRIPTION } from 'Constants/index'
import getUserInfo from 'lib/user-info'
import { loadCommentData, ICommentData } from 'Components/comment-3.0/service'
import getStaticLabels, { IStaticLabel } from 'Services/feed/static-labels'
import getCarIds from 'lib/get-cars-from-series'
import { replace } from 'New/article-detail/components/content'
import * as ZT from 'Constants/zt'
import * as LS from 'Constants/link-source'
import { replaceContent } from 'lib/keyword-replace'
import getCityName from 'lib/get-city-name'
import { getRedirectInfo, redirectTo } from 'lib/router-redirect'
import { getInnerLinkList } from 'Components/new/inner-link'
import { getOuterLinkList } from 'Components/new/outer-link'
interface IProps {
  is404?: boolean
  gid: string
  /**
   * 文章信息
   */
  article: IArticle

  /**
   * 是否是视频
   */
  isVideo: boolean

  subArticleType: number

  relatedRecommendData: IRelatedRecommendData

  relatedSeriesData: IRelatedSeriesItem[]

  recommendSeriesData: IRecommendSeriesItem[]

  commentData: ICommentData
  staticLabels: IStaticLabel[]
  fontStyleString?: string
}

function ArticleDetailPage({
  is404,
  gid,
  isVideo,
  article,
  subArticleType,
  relatedRecommendData,
  relatedSeriesData,
  recommendSeriesData,
  commentData,
  staticLabels,
  fontStyleString,
}: IProps) {
  if (is404) {
    return null
  }
  const pageTitle = article?.title ?? ''
  const desc = (article?.content ?? '')
    .replace(/\{!--.*?--\}/g, '')
    .replace(/<[^>]+>/g, '')
    .slice(0, 65)
  const description = `懂车帝提供${
    desc.length > 30
      ? desc
      : `${desc}。懂车帝提供信息最快最全的中国汽车网站，看车选车买车就上懂车帝。`
  }`

  const getGoogleStructureData = () => {
    const title = article?.title?.replace(/("|'|\\)/g, '')
    return {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: title,
      image: [
        article?.cover_image_info?.image_list
          ?.map((img: any) => img.image_url)
          .join(','),
      ],
      datePublished: new Date(article?.publish_time * 1000 || 0)?.toISOString(),
      author: [
        {
          '@type': 'Person',
          name: article?.media_user?.screen_name,
          url: `https://www.dongchedi.com/user/${article?.media_user?.id}`,
        },
      ],
    }
  }
  return (
    <>
      <Head>
        <title key="title">
          {`${pageTitle ? `${pageTitle}_懂车帝` : '懂车帝'}`}
        </title>
        <meta key="keywords" name="keywords" content={pageTitle} />
        <meta
          key="description"
          name="description"
          content={description ? description : DEFAULT_ARTICLE_DESCRIPTION}
        />
        <link
          rel="alternate"
          media="only screen and(max-width: 640px)"
          href={`https://m.dongchedi.com/article/${gid}`}
        />
        {!!article?.media_user?.id && (
          <script
            key="google-schema"
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getGoogleStructureData()),
            }}
          />
        )}
      </Head>
      <div className="article clearfix">
        <Article
          article={article}
          isVideo={isVideo}
          entryFeed={false}
          fontStyleString={fontStyleString}
          subArticleType={subArticleType}
          relatedRecommendData={relatedRecommendData}
          relatedSeriesData={relatedSeriesData}
          recommendSeriesData={recommendSeriesData}
          commentData={commentData}
          staticLabels={staticLabels}
        />
      </div>
    </>
  )
}

function handlePath(asPath: string) {
  const search = typeof asPath !== 'undefined' && asPath && asPath.split('?')[1]
  const queries = {} as any
  if (search) {
    search.split('&').forEach(function (c: string) {
      if (c === '') {
        return
      }
      const splitArr = c.split('=')
      const [key, value] = splitArr
      queries[key] = value
    })
  }
  return queries
}

// 为了匹配更多车系
// 同时匹配中英文括号 中文和数字
const charMap: Record<string, string> = {
  '(': '\\(|（',
  ')': '\\)|）',
  0: '0|零',
  1: '1|一',
  2: '2|二',
  3: '3|三',
  4: '4|四',
  5: '5|五',
  6: '6|六',
  7: '7|七',
  8: '8|八',
  9: '9|九',
}

/* eslint-disable complexity */
ArticleDetailPage.getInitialProps = async (ctx: any) => {
  const { query, asPath, req, res } = ctx

  // 如果gid过长，认为它不是一个正常的gid，这里30字符是一个预期异常的长度。
  if (query?.gid?.length > 30) {
    res.statusCode = 404
    return { is404: true }
  }
  const cityName = getCityName(asPath, req)
  let articleData
  try {
    const userData = getUserInfo(req)
    articleData = await getArticleDetail(
      {
        group_id: query?.gid,
        user_id: userData?.user_id,
        city_name: cityName,
      },
      req
    )
  } catch {
    // no-op
  }
  const isVisible =
    articleData?.article?.is_motor_article && articleData?.article?.is_visible
  const redirectInfo = getRedirectInfo({
    ctx,
    gid: query?.gid,
    webRid: articleData?.article?.web_rid,
    articleType: articleData?.article?.sub_article_type,
    isVisible,
    isLive: articleData?.article?.is_live,
  })
  if (redirectInfo.status === 404) {
    res.statusCode = 404
    return { is404: true }
  }
  if (redirectInfo.status !== 200) {
    const { zt } = handlePath(asPath)
    redirectTo({
      ctx,
      status: redirectInfo.status,
      url: `${redirectInfo.url}${zt ? `?zt=${zt}` : ''}`,
    })
    return
  }

  const allPromises: any[] = [
    Promise.resolve(articleData),
    getRelatedRecommend(req, query?.gid, 0, '', 20, 0),
    getStaticLabels({ name: '热点资讯' }, req),
  ]

  if (req?.isSpider) {
    allPromises.push(getRelatedSeries(req, query?.gid, '北京'))
    allPromises.push(getRecommendSeries(req, query?.gid, '北京'))
    allPromises.push(
      loadCommentData({
        gid: query?.gid,
        count: 10,
        currentPage: 0,
        req,
      })
    )
    allPromises.push(getInnerLinkList(asPath, req))
    allPromises.push(getOuterLinkList(asPath, req))
  }

  const data = (await Promise.allSettled(allPromises)).map((d) =>
    d.status === 'fulfilled' ? d.value : null
  )

  const article = data[0]?.article || {}

  // 排序：对于奥迪A4L和奥迪A4这种情况
  // 优先匹配到奥迪A4L时，忽略对奥迪A4的匹配
  const fullSeries = article?.relation_series_map || {}
  const fullSeriesNames = Object.keys(fullSeries).sort(
    (a, b) => b.length - a.length
  )

  const matchStart = Date.now()

  if (article.content && fullSeriesNames.length) {
    const regex = new RegExp(
      `(?:${fullSeriesNames
        .map(
          (d) => `(${d.replace(/[()0-9]/g, (char) => `(?:${charMap[char]})`)})`
        )
        .join('|')})`,
      'i'
    )
    const seriesList: {
      id: number
      name: string
      replaced: boolean
    }[] = []

    const nameMap: Record<string, boolean> = {}
    replace(article.content, regex, (match) => {
      let i = 1
      for (; i < match.length; i++) {
        if (match[i]) {
          break
        }
      }
      const name = fullSeriesNames[i - 1]
      seriesList.push({
        id: fullSeries[name],
        name: match[i],
        replaced: nameMap[name], // 标记去重
      })
      nameMap[name] = true
      return match[i]
    })

    const setSeriesIds = [...new Set(seriesList.map((d) => d.id))]

    if (seriesList.length) {
      const carIdsList = (
        await Promise.allSettled(
          setSeriesIds.map((d) => getCarIds(String(d), req))
        )
      ).map((d) => (d.status === 'fulfilled' ? d.value : null))

      const seriesIdCarIdsMap = setSeriesIds.reduce((map, d, index) => {
        map[d] = carIdsList[index]
        return map
      }, {} as Record<string, string | null>)

      let index = 0
      article.content = replace(article.content, regex, () => {
        const i = index
        index++
        const curSeries = seriesList[i]
        const seriesName = curSeries.name
        if (curSeries.replaced) {
          return seriesName
        }
        const seriesId = curSeries.id
        const carIds = seriesIdCarIdsMap[seriesId]?.split(',').join('-')
        if (!carIds) {
          return `<a href='/auto/series/${seriesId}' title=${seriesName} target="_blank">${seriesName}</a>(<a href='/auto/series/${seriesId}/images-wg' title=${seriesName}图片 target="_blank">图片</a>)`
        }
        return `<a href='/auto/series/${seriesId}' title=${seriesName} target="_blank">${seriesName}</a>(<a href='/auto/series/${seriesId}/images-wg' title=${seriesName}图片 target="_blank">图片</a>|<a href='/auto/params-carIds-${carIds}' title=${seriesName}配置 target="_blank" rel="nofollow">配置</a>|<a href='/dealer/${seriesId}?zt=${ZT.ZT_0014}&link_source=${LS.LS_0007}' title=${seriesName}询价 target="_blank" rel="opener nofollow">询价</a>)`
      })
    }
    if (req?.metrics?.emitTimer)
      req.metrics.emitTimer(
        'article-detail.match-series',
        Date.now() - matchStart
      )
  }

  const keywordDic = article.keyword_uri || {}
  let keywordList = Object.keys(keywordDic) || []

  if (article.content && keywordList.length) {
    keywordList = keywordList.sort((a, b) => b.length - a.length)
    const regex = new RegExp(
      `${keywordList
        ?.filter((word) => {
          return /[\u4e00-\u9fa5]/g.test(word)
        })
        ?.map((word) => `(${word.trim()})`)
        ?.join('|')}`,
      'gim'
    )

    let count = 0
    const length = article.content.match(/[\u4e00-\u9fa5]/g)?.length || 0
    let limit = 1
    if (length > 100 && length <= 500) limit = 5
    if (length > 500 && length <= 1000) limit = 10
    if (length > 1000) limit = 20
    const duplicateDic: { [key: string]: boolean } = {}

    article.content = replaceContent(article.content, (pureContent: string) => {
      return pureContent?.replace(regex, (keyword: string) => {
        if (count >= limit || duplicateDic[keyword]) {
          return keyword
        }
        count++
        duplicateDic[keyword] = true
        const uri = keywordDic[keyword]
        return `<a href='${uri}' target="_blank" title=${keyword}>${keyword}</a>`
      })
    })
  }

  if (article.content && (fullSeriesNames.length || keywordList.length)) {
    if (req?.metrics?.emitTimer)
      req.metrics.emitTimer(
        'article-detail.match-series',
        Date.now() - matchStart
      )
  }

  return {
    _pageType: 'new',
    gid: query.gid,
    article: isVisible ? data[0]?.article : {},
    isVideo: isVisible
      ? data[0]?.article?.article_type === 2
        ? true
        : false
      : false, //article_type 1-文章 2-视频
    subArticleType: data[0]?.article?.sub_article_type,
    relatedRecommendData: data?.[1],
    staticLabels: data?.[2],
    relatedSeriesData: data?.[3]?.data,
    recommendSeriesData: (data?.[4] as any)?.list,
    commentData: data?.[5],
    innerList: data?.[6],
    outerList: data?.[7],
    fontStyleString: data[0]?.article?.fontStyleString,
  }
}

export default ArticleDetailPage
